import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import ProfaseModal from '../../commons/ProfaseModal';
import PageForm, { FormField, SelectOption } from '../../commons/PageForm';
import PageAlert, { usePageAlert } from '../../commons/PageAlert';
import { useAppDispatch, useAppSelector } from '../../store';
import { createEntity } from '../../manager/workflow-def.reducer';
import { WORKFLOW_PERIODICIDAD_OPTIONS } from '../../shared/workflow-periodicidad';

const ACCENT = '#10b981';

interface WorkflowForm {
  nombre: string;
  periodicidad: string;
  periodo: string;
  porDefecto: string;
  executorClass: string;
  workflowClase: string;
}

const empty: WorkflowForm = {
  nombre: '',
  periodicidad: '',
  periodo: '',
  porDefecto: 'false',
  executorClass: '',
  workflowClase: '',
};

export default function WorkflowNewModal() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const updating      = useAppSelector(s => s.workflowDef.updating);
  const updateSuccess = useAppSelector(s => s.workflowDef.updateSuccess);
  const errorMessage  = useAppSelector(s => s.workflowDef.errorMessage) as string | null;

  const [form, setForm]             = useState<WorkflowForm>(empty);
  const [errors, setErrors]         = useState<Partial<Record<keyof WorkflowForm, string>>>({});
  const [claseOptions, setClaseOptions] = useState<SelectOption[]>([]);

  const { alert, showToast } = usePageAlert();
  const didSubmit = useRef(false);

  useEffect(() => {
    didSubmit.current = false;
    setForm(empty);
    setErrors({});
  }, []);

  useEffect(() => {
    axios
      .get('api/profase-workflow-clases?page=0&size=999&sort=nombre,asc')
      .then(res => {
        const opts: SelectOption[] = (res.data as any[]).map(c => ({
          value: String(c.id),
          label: `${c.nombre}`,
        }));
        setClaseOptions(opts);
      })
      .catch(() => setClaseOptions([]));
  }, []);

  useEffect(() => {
    if (errorMessage) {
      showToast('error', errorMessage);
    }
  }, [errorMessage, showToast]);

  useEffect(() => {
    if (updateSuccess && didSubmit.current) {
      didSubmit.current = false;
      navigate('/workflow');
    }
  }, [updateSuccess, navigate]);

  const fields = useMemo((): FormField<WorkflowForm>[] => [
    {
      name:        'nombre',
      label:       'Nombre',
      required:    true,
      placeholder: 'Nombre del workflow',
    },
    {
      name:     'periodicidad',
      label:    'Periodicidad',
      type:     'select',
      options:  WORKFLOW_PERIODICIDAD_OPTIONS,
    },
    {
      name:        'periodo',
      label:       'Período',
      type:        'number',
      placeholder: 'Ej: 1, 7, 30…',
    },
    {
      name:        'executorClass',
      label:       'Executor Class',
      placeholder: 'com.example.MyExecutor',
    },
    {
      name:    'workflowClase',
      label:   'Clase Workflow',
      type:    'select',
      options: claseOptions,
    },
    {
      name:  'porDefecto',
      label: 'Por Defecto',
      type:  'checkbox',
    },
  ], [claseOptions]);

  const close = () => navigate('/workflow');

  const handleFieldChange = (name: keyof WorkflowForm, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: Partial<Record<keyof WorkflowForm, string>> = {};
    if (!form.nombre.trim()) nextErrors.nombre = 'El nombre es requerido';
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    const payload: any = {
      nombre: form.nombre.trim(),
      periodicidad: form.periodicidad || null,
      periodo: form.periodo ? Number(form.periodo) : null,
      porDefecto: form.porDefecto === 'true',
      executorClass: form.executorClass || null,
      ...(form.workflowClase ? { workflowClase: { id: Number(form.workflowClase) } } : {}),
    };

    didSubmit.current = true;
    dispatch(createEntity(payload));
  };

  return (
    <ProfaseModal
      open
      titleId="workflow-new-modal-title"
      size="wide"
      scroll
    >
      <div style={s.header}>
        <h2 id="workflow-new-modal-title" className="profase-modal-title m-0">
          <FaProjectDiagram style={{ marginRight: 8, verticalAlign: '-2px' }} />
          Nuevo Workflow
        </h2>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm"
          onClick={close}
          disabled={updating}
        >
          Cerrar
        </button>
      </div>

      <p className="profase-modal-description">
        Complete los datos para crear un nuevo workflow.
      </p>

      {alert && <PageAlert alert={alert} />}

      <PageForm
        embedded
        fields={fields}
        values={form}
        errors={errors}
        isNew
        saving={updating}
        labelInline
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={close}
        accentColor={ACCENT}
        submitLabel="Crear workflow"
        cancelLabel="Cancelar"
      />
    </ProfaseModal>
  );
}

const s: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 8,
    flexWrap: 'wrap',
  },
};
